// Phase 15d — Reports generation (pure builder).
//
// Closes the "generation" half of the Phase 15 Reports gap, tied to
// the Route Engine: turns a deterministic strategy projection into a
// `goal_plan` report row. Everything is derived from the plan — no
// fabricated accuracy / win-rate / track record (accuracy &
// confidence stay null by construction; that was the single most
// severe doctrine breach the audit found on the Reports page). Every
// report carries an explicit "planning, not a promise" reasoning line
// (MASTER Non-Negotiable #9).
//
// Pure: no I/O, no user_id (the caller attaches auth.uid() at insert,
// enforced by the owner-checked INSERT policy from migration p15d).

import type {
  StrategyRoute,
  StrategyRoutePlan,
} from "../strategy/strategyRoute.ts";
import { STRATEGY_ROUTE_PROFILES } from "../strategy/strategyRoute.ts";

// Arrays are intentionally mutable: they land in jsonb columns whose
// generated `Json` insert type is non-readonly.
export interface StrategyReportInsert {
  readonly report_type: "goal_plan";
  readonly title: string;
  readonly summary: string;
  readonly status: "ready";
  readonly accuracy: null;
  readonly confidence: null;
  readonly metrics: { label: string; value: string }[];
  readonly reasoning: string[];
  readonly slip_id: null;
  readonly chain_id: null;
}

const HONESTY_LINE =
  "Planning assumptions only — not historical performance and not a " +
  "promise of any outcome (MASTER Non-Negotiable #9).";

function money(n: number): string {
  return n.toFixed(2);
}
function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

export function buildStrategyReportInsert(
  plan: StrategyRoutePlan,
  route: StrategyRoute,
): StrategyReportInsert {
  const profile = STRATEGY_ROUTE_PROFILES[route];

  const feasibilityClause = plan.feasible && plan.estimatedWeeks !== null
    ? `projected to reach target in ${plan.estimatedWeeks} week(s)`
    : `does not reach target within ${plan.maxWeeks} week(s)`;

  const summary =
    `${profile.label} route · target ${money(plan.targetAmount)}, ` +
    `opening stake ${money(plan.openingStake)} — ${feasibilityClause}. ` +
    `Chain probability ${pct(plan.fullChainProbability)}.`;

  return {
    report_type: "goal_plan",
    title: `Strategy plan — ${profile.label}`,
    summary,
    status: "ready",
    // Never fabricate a track record. We have no settled-results
    // accuracy for a forward-looking plan, so these stay null.
    accuracy: null,
    confidence: null,
    metrics: [
      {
        label: "Estimated weeks",
        value: plan.estimatedWeeks === null
          ? "—"
          : String(plan.estimatedWeeks),
      },
      { label: "Chain probability", value: pct(plan.fullChainProbability) },
      {
        label: "Projected final",
        value: money(plan.projectedFinalAmount),
      },
      {
        label: "Protected at bust",
        value: money(plan.protectedAmountAtBust),
      },
      { label: "Target odds", value: String(plan.targetOdds) },
      { label: "Feasible", value: plan.feasible ? "yes" : "no" },
    ],
    reasoning: [
      profile.intent,
      plan.downsideSummary,
      ...plan.warnings,
      HONESTY_LINE,
    ],
    slip_id: null,
    chain_id: null,
  };
}
